/**
 * Page Paramétrage des Rubriques de Paie (Admin)
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Receipt, Plus, Sparkles, Trash2, Edit3, Lock } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const KIND_LABEL = {
  GAIN: 'Gain',
  DEDUCTION: 'Retenue',
  EMPLOYEE_CONTRIBUTION: 'Cotisation salarié',
  EMPLOYER_CONTRIBUTION: 'Cotisation patronale',
};
const KIND_BADGE = {
  GAIN: 'bg-emerald-100 text-emerald-700',
  DEDUCTION: 'bg-amber-100 text-amber-700',
  EMPLOYEE_CONTRIBUTION: 'bg-blue-100 text-blue-700',
  EMPLOYER_CONTRIBUTION: 'bg-violet-100 text-violet-700',
};
const BASIS_OPTS = [
  { v: 'BASE_SALARY', l: 'Salaire de base' },
  { v: 'FIXED', l: 'Montant fixe' },
  { v: 'PERCENT', l: 'Pourcentage' },
  { v: 'FORMULA', l: 'Formule' },
  { v: 'BRACKET', l: 'Tranches (impôt)' },
  { v: 'VARIABLE', l: 'Variable mensuelle' },
];
const OF_OPTS = [
  { v: 'BASE_SALARY', l: 'Salaire de base' },
  { v: 'GROSS_TAXABLE', l: 'Salaire imposable' },
  { v: 'TAXABLE', l: 'Net imposable' },
  { v: 'GROSS', l: 'Salaire brut' },
];

const emptyForm = {
  code: '', name: '', kind: 'GAIN', basis: 'FIXED', order: 100,
  amount: '', percent: '', of: 'BASE_SALARY', formula: '',
  isTaxable: false, feedsGross: true, isActive: true, country: 'ML',
};

function RubricDialog({ open, onOpenChange, rubric, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = !!rubric;

  useEffect(() => {
    if (rubric) {
      setForm({
        ...emptyForm,
        ...rubric,
        amount: rubric.amount ?? '',
        percent: rubric.percent ?? '',
        formula: rubric.formula ?? '',
      });
    } else {
      setForm(emptyForm);
    }
  }, [rubric, open]);

  const submit = async () => {
    if (!form.code || !form.name) {
      toast.error('Code et libellé requis');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        order: parseInt(form.order, 10) || 100,
        amount: form.amount === '' ? null : parseFloat(form.amount),
        percent: form.percent === '' ? null : parseFloat(form.percent),
        formula: form.formula || null,
      };
      if (isEdit) {
        await api.put(`/payroll/rubrics/${rubric.id}`, payload);
        toast.success('Rubrique mise à jour');
      } else {
        await api.post('/payroll/rubrics', payload);
        toast.success('Rubrique créée');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rubric-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier la rubrique' : 'Nouvelle rubrique'}</DialogTitle>
          <DialogDescription>Définissez le mode de calcul et le comportement.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div>
            <Label>Code *</Label>
            <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              data-testid="rubric-code" placeholder="PRIME_PERF" disabled={isEdit && rubric?.isMandatory} />
          </div>
          <div>
            <Label>Libellé *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              data-testid="rubric-name" placeholder="Prime de performance" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="rubric-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Base de calcul</Label>
            <Select value={form.basis} onValueChange={v => setForm({ ...form, basis: v })}>
              <SelectTrigger data-testid="rubric-basis"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BASIS_OPTS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.basis === 'FIXED' && (
            <div>
              <Label>Montant</Label>
              <Input type="number" value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })} />
            </div>
          )}
          {form.basis === 'PERCENT' && (
            <>
              <div>
                <Label>Taux (%)</Label>
                <Input type="number" step="0.01" value={form.percent}
                  onChange={e => setForm({ ...form, percent: e.target.value })} />
              </div>
              <div>
                <Label>Appliqué sur</Label>
                <Select value={form.of} onValueChange={v => setForm({ ...form, of: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OF_OPTS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {form.basis === 'FORMULA' && (
            <div className="col-span-2">
              <Label>Formule</Label>
              <Input value={form.formula} onChange={e => setForm({ ...form, formula: e.target.value })}
                placeholder="base * 0.02 * min(years, 25)" />
              <p className="text-xs text-slate-500 mt-1">Variables: <code>base, gross, taxable, years</code></p>
            </div>
          )}
          <div>
            <Label>Ordre</Label>
            <Input type="number" value={form.order}
              onChange={e => setForm({ ...form, order: e.target.value })} />
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.isTaxable}
                onCheckedChange={v => setForm({ ...form, isTaxable: !!v })} /> Imposable
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.feedsGross}
                onCheckedChange={v => setForm({ ...form, feedsGross: !!v })} /> Alimente brut
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.isActive}
                onCheckedChange={v => setForm({ ...form, isActive: !!v })} /> Active
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="rubric-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="rubric-submit">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PayrollRubricsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/payroll/rubrics');
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedML = async () => {
    try {
      setSeeding(true);
      const { data } = await api.post('/payroll/rubrics/seed-ml');
      toast.success(`${data.added} rubrique(s) Mali ajoutée(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSeeding(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer "${r.name}" ?`)) return;
    try {
      await api.delete(`/payroll/rubrics/${r.id}`);
      toast.success('Supprimée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="payroll-rubrics-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Receipt className="w-7 h-7 text-indigo-600" /> Rubriques de Paie
            </h1>
            <p className="text-slate-500 mt-1">Configuration du moteur de calcul (gains, retenues, cotisations).</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={seedML} disabled={seeding} data-testid="seed-ml-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              {seeding ? 'Import…' : 'Seed Mali (INPS/AMO/IUTS)'}
            </Button>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-rubric-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle rubrique
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle>Liste des rubriques ({items.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-500 py-8">Chargement…</p>
            ) : items.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Receipt className="w-12 h-12 mx-auto text-slate-300" />
                <p className="mt-3 text-slate-600 font-medium">Aucune rubrique configurée</p>
                <p className="text-sm text-slate-400 mt-1">Cliquez "Seed Mali" pour importer le pack par défaut.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="text-left py-2 px-3">Ordre</th>
                      <th className="text-left py-2 px-3">Code</th>
                      <th className="text-left py-2 px-3">Libellé</th>
                      <th className="text-left py-2 px-3">Type</th>
                      <th className="text-left py-2 px-3">Calcul</th>
                      <th className="text-left py-2 px-3">Statut</th>
                      <th className="text-right py-2 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(r => (
                      <tr key={r.id} className="border-t hover:bg-slate-50/50" data-testid={`rubric-row-${r.code}`}>
                        <td className="py-2 px-3 text-slate-500">{r.order}</td>
                        <td className="py-2 px-3 font-mono text-xs">{r.code}</td>
                        <td className="py-2 px-3 font-medium">{r.name}</td>
                        <td className="py-2 px-3">
                          <Badge className={KIND_BADGE[r.kind] || ''}>{KIND_LABEL[r.kind] || r.kind}</Badge>
                        </td>
                        <td className="py-2 px-3 text-slate-600">
                          {r.basis === 'FIXED' && `${r.amount?.toLocaleString('fr-FR')} XOF`}
                          {r.basis === 'PERCENT' && `${r.percent}% / ${r.of || ''}`}
                          {r.basis === 'BRACKET' && `Tranches (${r.brackets?.length || 0})`}
                          {r.basis === 'FORMULA' && <code className="text-xs">{r.formula}</code>}
                          {r.basis === 'BASE_SALARY' && 'Salaire base'}
                          {r.basis === 'VARIABLE' && 'Variable'}
                        </td>
                        <td className="py-2 px-3">
                          {r.isActive
                            ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                            : <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>}
                          {r.isMandatory && <Lock className="inline w-3 h-3 ml-2 text-slate-400" />}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(r); setDialogOpen(true); }}
                            data-testid={`edit-rubric-${r.code}`}>
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          {!r.isMandatory && (
                            <Button variant="ghost" size="sm" onClick={() => remove(r)}
                              data-testid={`delete-rubric-${r.code}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <RubricDialog open={dialogOpen} onOpenChange={setDialogOpen} rubric={editing} onSaved={load} />
      </div>
    </DashboardLayout>
  );
}
